import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Seo from '../../components/Seo'
import { api, type BookingDetails, type BookingSlot } from '../../api/client'

export default function Booking() {
  const location=useLocation()
  const token=new URLSearchParams(location.hash.slice(1)).get('token') ?? ''
  const [details,setDetails]=useState<BookingDetails|null>(null)
  const [slots,setSlots]=useState<BookingSlot[]>([])
  const [selected,setSelected]=useState('')
  const [error,setError]=useState('')
  const [busy,setBusy]=useState(false)
  const [searched,setSearched]=useState(false)
  const pending=useRef(false)
  const generation=useRef(0)
  useEffect(()=>{
    const current=++generation.current
    pending.current=false
    setDetails(null);setSlots([]);setSelected('');setError('');setBusy(false);setSearched(false)
    if(!token){setError('Open the booking link from your email.');return}
    api.bookingDetails(token).then(value=>{if(generation.current===current)setDetails(value)})
      .catch(e=>{if(generation.current===current)setError(e.message)})
    return()=>{generation.current+=1}
  },[token])
  const when=(value:string)=>new Date(value).toLocaleString(undefined,{timeZone:details?.time_zone,dateStyle:'full',timeStyle:'short'})
  async function findTimes(){
    if(pending.current || !details || details.status==='confirmed')return
    const current=generation.current
    pending.current=true;setBusy(true);setError('');setSelected('');setSlots([])
    const start=new Date(),end=new Date(start.getTime()+30*86400000)
    try {
      const result=await api.bookingSlots(token,start.toISOString(),end.toISOString())
      if(generation.current===current){setSlots(result.slots);setSearched(true)}
    } catch(e) {
      if(generation.current===current)setError(e instanceof Error?e.message:'Could not load available times.')
    } finally {
      if(generation.current===current){pending.current=false;setBusy(false)}
    }
  }
  async function confirm(){
    if(pending.current || !details || details.status==='confirmed' || !slots.some(slot=>slot.start===selected))return
    const current=generation.current
    pending.current=true;setBusy(true);setError('')
    try {
      const result=await api.confirmBooking(token,selected)
      if(generation.current===current)setDetails(result)
    } catch(e) {
      if(generation.current===current)setError(e instanceof Error?e.message:'Could not confirm the booking. You can retry the same time.')
    } finally {
      if(generation.current===current){pending.current=false;setBusy(false)}
    }
  }
  return <><Seo title="Choose a meeting time" noindex/>
    <section className="hero"><h1>Choose a meeting time</h1></section>
    <section className="section" style={{maxWidth:'44rem'}}>
      {error && <p className="notice crit" role="alert">{error} Reply to your email if you need help.</p>}
      {details?.status==='confirmed' ? <div role="status">
        <h2>Booking confirmed</h2>
        {details.meeting && <p>Your booking was confirmed for <strong>{when(details.meeting.start)}</strong> ({details.meeting.time_zone}).</p>}
        <p>Check your email at {details.masked_recipient} for the calendar invitation and any later changes.</p>
      </div> : details ? <>
        <p>Choose a {details.duration_minutes}-minute call with Ledgerline Procurement. Your invitation will go to {details.masked_recipient}.</p>
        <p>All times below are in <strong>{details.time_zone}</strong>. Your time is reserved only after you confirm.</p>
        <button type="button" className="btn secondary" disabled={busy} onClick={()=>void findTimes()}>{busy?'Working…':searched?'Refresh available times':'Find available times'}</button>
        {searched && !slots.length && !busy && <p>No available times were found in the next 30 days. Reply to your email to arrange another time.</p>}
        {!!slots.length && <form onSubmit={e=>{e.preventDefault();void confirm()}}>
          <fieldset disabled={busy} style={{border:0,padding:'1rem 0'}}><legend>Available times</legend>
            {slots.map(slot=><label key={slot.start} style={{display:'block',padding:'.75rem 0'}}><input type="radio" name="booking-time" value={slot.start} checked={selected===slot.start} onChange={()=>setSelected(slot.start)}/> {when(slot.start)}</label>)}
          </fieldset>
          {selected && <p>Confirm <strong>{when(selected)}</strong> ({details.time_zone}), for {details.duration_minutes} minutes. The invitation will be sent to {details.masked_recipient}.</p>}
          <button className="btn" disabled={busy || !selected}>{busy?'Confirming…':'Confirm booking'}</button>
        </form>}
        <p style={{marginTop:'1.5rem'}}>This link expires {when(details.expires_at)} ({details.time_zone}).</p>
      </> : !error && <p role="status">Loading booking details…</p>}
    </section>
  </>
}
